import { TuiSkeleton } from '@taiga-ui/kit';
import { TuiSwipeActions } from '@taiga-ui/addon-mobile';
import { pure } from '@core/helpers/pure';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  Renderer2,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { TuiHint, TuiCheckbox, TuiDialogService } from '@taiga-ui/core';
import { RequestModel } from '@core/models/request.model';
import { RequestProductIconSize } from '@features/requests/request-product-icon/types';
import { RequestReleaseTimeParserPipe } from '@core/pipes/request-release-time-parser.pipe';

import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { FileDownloadService } from '@core/services/file-download.service';
import { IsLoadingService } from '@core/services/is-loading.service';
import { RequestFilesService } from '@core/services/request/request-files.service';
import { RequestFiltersService } from '@core/services/request/request-filters.service';
import { RequestService } from '@core/services/request/request.service';
import { XAlertService } from '@core/services/x-alert.service';
import {
  AsyncPipe,
  DatePipe,
  NgClass,
  NgOptimizedImage,
  NgTemplateOutlet,
} from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import {
  counterpartyIconResolver,
  hexToRgba,
  leaseProductIconResolver,
  Nullable,
} from '@core/helpers';
import { TranslatePipe } from '@ngx-translate/core';
import { IconButtonComponent, SvgIconComponent } from '@common/components';
import { RequestProductIconComponent } from '@features/requests/request-product-icon/request-product-icon.component';
import { WA_WINDOW, WA_LOCATION } from '@ng-web-apis/common';
import { ClipboardService } from 'ngx-clipboard';
import {
  Counterparty,
  RequestFile,
  RequestsListItemTranslations,
  RequestsListItemTranslationsPayload,
  TextCopierTranslations,
  TextCopierTranslationsPayload,
} from '@core/types';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { environment } from '@environment/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SelectedIds } from '@common/types';
import { OutkeepPermission } from '@outkeep/shared';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { TemporaryAccessDialogComponent } from '../temporary-access-dialog/temporary-access-dialog.component';

@Component({
  selector: 'x-request-list-item-mobile',
  templateUrl: './request-list-item-mobile.component.html',
  imports: [
    TuiSkeleton,
    SvgIconComponent,
    RequestReleaseTimeParserPipe,
    TuiCheckbox,
    FormsModule,
    RouterLink,
    NgClass,
    AsyncPipe,
    DatePipe,
    TranslatePipe,
    IconButtonComponent,
    RequestProductIconComponent,
    NgTemplateOutlet,
    TuiSwipeActions,
    NgOptimizedImage,
    TuiHint,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestListItemMobileComponent implements OnInit, OnDestroy {
  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);
  public readonly requestService = inject(RequestService);
  private readonly xAlertService = inject(XAlertService);
  private readonly clipboardService = inject(ClipboardService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly requestFilesService = inject(RequestFilesService);
  private readonly requestFiltersService = inject(RequestFiltersService);
  private readonly fileDownloadService = inject(FileDownloadService);
  private readonly permissionService = inject(OutkeepPermissionService);
  private readonly dialogService = inject(TuiDialogService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly renderer: Renderer2 = inject(Renderer2);
  private readonly window = inject(WA_WINDOW);
  protected readonly location = inject(WA_LOCATION);
  $request = input.required<RequestModel>({ alias: 'request' });
  $selectedRequestsIds: Signal<SelectedIds | null> | undefined = undefined;

  public locale = this.outkeepTranslateService.getLocaleStr();

  public lPPhoto: WritableSignal<Nullable<RequestFile>> = signal(null);
  public ePTS: WritableSignal<Nullable<RequestFile>> = signal(null);
  public appKDkp: WritableSignal<Nullable<RequestFile>> = signal(null);
  public appKDl: WritableSignal<Nullable<RequestFile>> = signal(null);

  public selectRequestEvent = output<number>();

  public mobileRequestsListCompactMode =
    this.requestFiltersService.$mobileRequestsListCompactMode;

  readonly canCreateTemporaryAccess = this.permissionService.$hasPermission(
    OutkeepPermission.LogistCreateTemporaryAccess,
  );

  protected readonly RequestProductIconSize = RequestProductIconSize;

  private translates: Partial<RequestsListItemTranslationsPayload> &
    TextCopierTranslationsPayload = {
    [RequestsListItemTranslations.EptsNotFound]: 'Е-ПТС не найден',
    [TextCopierTranslations.Copied]: 'Скопировано',
    [TextCopierTranslations.NotFoundToCopy]: 'Нечего копировать',
    [RequestsListItemTranslations.DocNotFound]: 'Документ не найден',
  };

  constructor() {
    effect(() => {
      if (this.requestFilesService.loadedRequestsMainFiles()) {
        const mainFiles =
          this.requestFilesService.loadedRequestsMainFiles()[
            this.$request().id
          ];

        if (mainFiles) {
          this.appKDkp.set(
            mainFiles['obrz_app_k_dkp']?.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            )?.[0] ?? null,
          );
          this.appKDl.set(
            mainFiles['obrz_app_k_dl']?.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            )?.[0] ?? null,
          );
          this.lPPhoto.set(
            mainFiles['photo_pl']?.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            )?.[0] ?? null,
          );
          this.ePTS.set(
            mainFiles['obrz_pts_psm']?.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            )?.[0] ?? null,
          );
        }
      }
    });
  }

  ngOnInit(): void {
    this.$selectedRequestsIds = computed(() =>
      this.requestService.selectedRequestsIds(),
    );

    this.outkeepTranslateService
      .translate([
        RequestsListItemTranslations.EptsNotFound,
        RequestsListItemTranslations.DocNotFound,
        ...Object.values(TextCopierTranslations),
      ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (
          translates: RequestsListItemTranslationsPayload &
            TextCopierTranslationsPayload,
        ) => (this.translates = translates),
      );
  }

  ngOnDestroy(): void {
    this.$selectedRequestsIds = undefined;
  }

  onSelectRequest() {
    this.selectRequestEvent.emit(this.$request().id);
  }

  protected readonly hexToRgba = hexToRgba;

  public createLink(doc: 'epts' | 'lPPhoto' | 'appKDl' | 'appKDkp'): void {
    let file: Nullable<RequestFile> = null;

    switch (doc) {
      case 'epts':
        file = this.ePTS();
        break;
      case 'lPPhoto':
        file = this.lPPhoto();
        break;
      case 'appKDl':
        file = this.appKDl();
        break;
      case 'appKDkp':
        file = this.appKDkp();
        break;
      default:
        break;
    }

    if (!file) {
      this.xAlertService.error(
        this.translates[RequestsListItemTranslations.DocNotFound]!,
      );

      return;
    }

    if (file.filename.toLowerCase().endsWith('.pdf')) {
      this.fileDownloadService.openPdfByPath(
        file.path,
        file.filename || file.path.split('/').pop() || file.path,
      );

      return;
    }

    this.fileDownloadService.downloadFile(
      environment.downloadFilesUrl + file!.path,
      file!.filename || file!.path.split('/').pop() || file!.path,
      false,
      this.renderer,
    );
  }

  public openYaNav() {
    this.window.open(
      `yandexnavi://map_search?text=${this.$request().deliveryAddress}`,
      '_blank',
    );
  }

  public copyToClipboard(event: Event, text: string): void {
    event.stopPropagation();
    this.clipboardService.copy(text);
    if (!text) {
      this.xAlertService.showMessage(
        this.translates[TextCopierTranslations.NotFoundToCopy],
        {
          appearance: 'warning',
          autoClose: 2000,
        },
      );

      return;
    } else {
      this.xAlertService.showMessage(
        this.translates[TextCopierTranslations.Copied],
        {
          appearance: 'positive',
          autoClose: 2000,
        },
      );
    }
  }

  @pure
  public requestIconResolver(cpiD: Counterparty['id']) {
    return counterpartyIconResolver(cpiD);
  }

  @pure
  public requestLeaseProductIconResolver(
    carType?: RequestModel['carType'],
    carModel?: RequestModel['carModel'],
  ) {
    return leaseProductIconResolver(carType, carModel);
  }

  shareRequestLink(): void {
    const request = this.$request();
    const shareData = {
      title: request.contract || request.requestNumber,
      text: `Номер заявки: ${request.requestNumber}\nНомер проекта: ${request.projectNumber}\nДоговор: ${request.contract}\nСтатус: ${request.statusId!.name}\nСоздана: ${this.formatDateWithLocale(this.$request().releaseDate)}\nЗаказчик: ${request.counterPartyName}\nVIN: \`\`\`${request.vin}\`\`\`\nУстановщик: ${request.installerShort} ${this.lPPhoto() ? `\nФото ПЛ: ${this.location.origin + environment.downloadFilesUrl + this.lPPhoto()!.path}` : ''} ${this.ePTS() ? `\nЕ-ПТС: ${this.location.origin + environment.downloadFilesUrl + this.ePTS()!.path}` : ''}\nURL:\`${this.location.origin}/requests/${request.id}\``,
      url: `${this.location.origin}/requests/${request.id}`,
    };

    void navigator.share(shareData);
  }

  openTemporaryAccessDialog(requestId: number): void {
    this.dialogService
      .open(new PolymorpheusComponent(TemporaryAccessDialogComponent), {
        data: { requestIds: [requestId] },
        dismissible: true,
        size: null,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private formatDateWithLocale(stringDate?: string | null): string {
    if (stringDate) {
      return new Date(stringDate).toLocaleDateString(this.locale, {
        day: '2-digit',
        weekday: 'long',
        month: 'long',
        year: 'numeric',
      });
    } else {
      return '';
    }
  }
}
